import { useCallback, useMemo, useState } from 'react';
import { delegationAPI } from '../../lib/api';
import type { DelegateOption, UserDelegation, UserDelegationInput } from '../../lib/api';
import { getErrorMessage, useFetchData } from '../../hooks/useFetchData';
import { toast } from 'sonner';

interface OutOfOfficeData {
  covers: UserDelegation[];
  colleagues: DelegateOption[];
  today: string;
}

export interface UseOutOfOfficeResult {
  loading: boolean;
  error: string | null;
  /** Today (YYYY-MM-DD) at the user's company — the server's "today". */
  today: string;
  /** Every time away the user planned at this company, newest first. */
  mine: UserDelegation[];
  /** The time away on today, if any. */
  current: UserDelegation | null;
  /** The next time away that has not started yet. */
  next: UserDelegation | null;
  /** Time away on now or coming up where the user is the one covering. */
  covering: UserDelegation[];
  /** Colleagues the user may pick: they hold every approval right the user holds. */
  colleagues: DelegateOption[];
  saving: boolean;
  /** Plan (no id) or change time away. Resolves null when saved, else the reason it wasn't. */
  save: (input: UserDelegationInput, id?: number) => Promise<string | null>;
  /** Turn time away off now, or cancel it. Resolves null when done, else the reason it wasn't. */
  end: (cover: UserDelegation) => Promise<string | null>;
  refetch: () => Promise<void>;
}

const OPEN = new Set(['active', 'scheduled']);

/** Largest page the API serves; nobody plans this much time away. */
const PAGE_SIZE = 100;

/**
 * CR-5889: loads and changes the signed-in user's out-of-office cover.
 *
 * Reads the user's time away at their active company, the colleagues they may
 * pick and the company's date in one go, and refreshes them after every
 * change so the switch, the lists and the picker never disagree. Save and end
 * hand the server's reason back to the caller to show next to the form.
 *
 * @param userId - The signed-in user's id; time away is split into theirs and
 *   the time away they cover by it.
 * @returns The user's time away, the time away they cover, the eligible
 *   colleagues, the company's date, and save/end actions.
 *
 * @example
 *   const { current, colleagues, save } = useOutOfOffice(user?.id);
 */
export function useOutOfOffice(userId: number | undefined): UseOutOfOfficeResult {
  const [saving, setSaving] = useState(false);
  const { data, loading, error, refetch } = useFetchData<OutOfOfficeData>(
    async () => {
      const [covers, eligible] = await Promise.all([
        delegationAPI.list({ page_size: PAGE_SIZE }),
        delegationAPI.eligibleDelegates(),
      ]);
      return {
        covers: covers.data.results,
        colleagues: eligible.data.colleagues,
        today: eligible.data.today,
      };
    },
    { deps: [userId] },
  );

  const covers = useMemo(() => data?.covers ?? [], [data]);
  const mine = useMemo(() => covers.filter((c) => c.user === userId), [covers, userId]);
  const current = useMemo(() => mine.find((c) => c.status === 'active') ?? null, [mine]);
  const next = useMemo(
    () => mine
      .filter((c) => c.status === 'scheduled')
      .sort((a, b) => a.start_date.localeCompare(b.start_date))[0] ?? null,
    [mine],
  );
  const covering = useMemo(
    () => covers.filter((c) => c.delegate_user === userId && OPEN.has(c.status)),
    [covers, userId],
  );

  const save = useCallback(async (input: UserDelegationInput, id?: number) => {
    setSaving(true);
    try {
      if (id) {
        await delegationAPI.update(id, input);
        toast.success('Time away updated');
      } else {
        await delegationAPI.create(input);
        toast.success('Time away saved');
      }
      await refetch();
      return null;
    } catch (err: unknown) {
      return getErrorMessage(err, 'Your time away could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [refetch]);

  const end = useCallback(async (cover: UserDelegation) => {
    setSaving(true);
    try {
      await delegationAPI.end(cover.id);
      toast.success(cover.status === 'scheduled' ? 'Time away cancelled' : 'Out of office turned off');
      await refetch();
      return null;
    } catch (err: unknown) {
      return getErrorMessage(err, 'Your time away could not be turned off.');
    } finally {
      setSaving(false);
    }
  }, [refetch]);

  return {
    loading,
    error,
    today: data?.today ?? '',
    mine,
    current,
    next,
    covering,
    colleagues: data?.colleagues ?? [],
    saving,
    save,
    end,
    refetch,
  };
}
